import { productApis, uploadApis, uploadV2Apis } from "@payping/api";
import type { PermanentDetailViewModel, ProductFullDeatilViewModel } from "@payping/api";
import { CuToman, DownOutlined } from "@payping/icons";
import { getPermalinkDetails, getProductDetails } from "@payping/redux/actions";
import { dateFormats, toJalali } from "@payping/utils/dateUtils";
import {
  Badge,
  Button,
  Col,
  Descriptions,
  Drawer,
  Dropdown,
  Flex,
  Image,
  Modal,
  QRCode,
  Row,
  Skeleton,
  Space,
  Typography,
  usePaypingContext,
} from "@payping/web-components";
import qs from "query-string";
import { Dispatch, FC, SetStateAction } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAsync, useAsyncFn, useAsyncRetry } from "react-use";
import { useAppDispatch, useAppTranslation } from "@/utils";
import { DashboardRoutes } from "@/router";
import { LocationStateSearch } from "./types";
import { buildUrl } from "@payping/utilities-shared/url";
import { URLS } from "@payping/utilities-shared/consts";

interface Props {
  retryList: () => void;
  code: string;
  setCode: Dispatch<SetStateAction<string | undefined>>;
}

const ProductDetail: FC<Props> = (props) => {
  const { code, setCode, retryList } = props;
  const location = useLocation();
  const locationSearch = (location.state as LocationStateSearch)?.locationSearch;
  const { t } = useAppTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [modal, contextHolder] = Modal.useModal();
  const { breakpoints } = usePaypingContext();
  const { md } = breakpoints;

  const {
    loading: loadingProduct,
    retry,
    value: productDetails,
  } = useAsyncRetry(async (): Promise<ProductFullDeatilViewModel | undefined> => {
    try {
      return dispatch(getProductDetails(code));
    } catch {
      /* empty */
    }
    return undefined;
  }, [code, dispatch]);
  const { loading: loadingLink, value: linkDetails } = useAsync(async (): Promise<
    PermanentDetailViewModel | undefined
  > => {
    try {
      return dispatch(getPermalinkDetails(code, { isSilent: true }));
    } catch {
      /* empty */
    }
    return undefined;
  }, [code, dispatch]);

  const [, handlePdf] = useAsyncFn(
    async (newWindow: Window | null) => {
      const path = await uploadV2Apis.productBuyersWithProductCodeGet(code);
      newWindow?.location?.assign?.(path);
    },
    [code],
  );

  const [, handleExcel] = useAsyncFn(
    async (newWindow: Window | null) => {
      const path = await uploadApis.productBuyersExcelWithProductCodeGet(code);
      newWindow?.location?.assign?.(path);
    },
    [code],
  );

  const buttons = (
    <Space>
      <Dropdown
        key={2}
        menu={{
          items: [
            {
              key: 0,
              label: productDetails?.isActive ? t("operations.disable") : t("operations.enable"),
              onClick: async () => {
                await productApis.productChangeActivation(code);
                retry();
                retryList();
              },
            },
            {
              key: 1,
              label: productDetails?.isArchived ? t("operations.restore") : t("operations.archive"),
              onClick: () => {
                modal.confirm({
                  cancelText: t("cancel"),
                  content: t("product.confirm.Archive"),
                  mask: { closable: true },
                  okText: t("ok"),
                  onOk: async () => {
                    await productApis.productChangeArchive(code);
                    await retry();
                    await retryList();
                  },
                });
              },
            },
            ...(linkDetails?.buyerCounts
              ? [
                  {
                    key: 2,
                    label: t("operations.gotoReport"),
                    onClick: () => {
                      navigate({
                        pathname: DashboardRoutes.mySale.report.url,
                        search: qs.stringify({ clientId: "PaypingProduct", clientRefId: code }),
                      });
                    },
                  },
                  {
                    key: 3,
                    label: `${t("report.title")} ${t("excel")}`,
                    onClick: () => {
                      const newWindow = window.open(undefined, "_blank");
                      handleExcel(newWindow);
                    },
                  },
                  {
                    key: 4,
                    label: `${t("report.title")} ${t("pdf")}`,
                    onClick: () => {
                      const newWindow = window.open(undefined, "_blank");
                      handlePdf(newWindow);
                    },
                  },
                ]
              : []),
          ],
        }}
      >
        <Button type={"primary"} ghost icon={<DownOutlined width={14} height={14} />} iconPosition="end">
          {t("operation")}
        </Button>
      </Dropdown>
      <Link
        to={{
          pathname: DashboardRoutes.myShop.product.edit.url.replace(":code", code),
        }}
        state={{ locationSearch }}
        key={1}
      >
        <Button type={"primary"}>{t("edit")}</Button>
      </Link>
    </Space>
  );

  const qrLink = buildUrl(URLS.PPNG_URL, "d", productDetails?.code || "").toString();

  enum LinkQuestionOptions {
    no,
    optional,
    mandatory,
  }

  return (
    <Drawer
      extra={buttons}
      title={t("pageTitles.itemDetail")}
      placement={md ? "left" : "bottom"}
      open={Boolean(code)}
      size={md ? undefined : "100%"}
      onClose={() => setCode(undefined)}
    >
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          {loadingProduct ? (
            <Skeleton active paragraph />
          ) : (
            <Descriptions className="tw:w-full" column={1} bordered title={<>&nbsp;</>}>
              <Descriptions.Item label={t("common.status")}>
                {productDetails?.isActive ? (
                  <Badge status={"success"} text={t("active")} />
                ) : (
                  <Badge status={"default"} text={t("inactive")} />
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t("common.operation.archive")}>
                {productDetails?.isArchived ? t("archived") : t("notArchived")}
              </Descriptions.Item>
              <Descriptions.Item label={t("product.form.upload.label")}>
                <div className="tw:h-18 tw:w-18">
                  <Image key={0} width={"100%"} height={"100%"} src={productDetails?.imageLink} />
                </div>
              </Descriptions.Item>
              {linkDetails?.isActive ? (
                <>
                  <Descriptions.Item label={"QRCode"}>
                    <div className="tw:h-18 tw:w-18 tw:flex-col tw:items-center tw:justify-between">
                      <QRCode size={72} className={"detail-image-qr"} value={qrLink} />
                    </div>
                  </Descriptions.Item>
                  <Descriptions.Item label={t("link.title")}>
                    <Typography.Link href={qrLink} target={"_blank"} rel={"noreferrer"}>
                      {qrLink}
                    </Typography.Link>
                  </Descriptions.Item>
                </>
              ) : null}
            </Descriptions>
          )}
        </Col>
        <Col xs={24}>
          {loadingProduct ? (
            <Skeleton active paragraph />
          ) : (
            <Descriptions className="tw:w-full" column={1} bordered title={t("product.detail.specification")}>
              <Descriptions.Item label={t("product.name")}>{productDetails?.title}</Descriptions.Item>
              <Descriptions.Item label={t("product.table.code")}>{productDetails?.code}</Descriptions.Item>
              <Descriptions.Item label={t("description")}>{productDetails?.description}</Descriptions.Item>
              <Descriptions.Item label={t("product.form.price.title")}>
                {productDetails?.amount ? (
                  <Flex align="center">
                    <Typography.Text type="secondary">{String(productDetails?.amount)}</Typography.Text>
                    <CuToman width={24} height={24} />
                  </Flex>
                ) : (
                  t("product.form.price.byUser")
                )}
              </Descriptions.Item>

              <Descriptions.Item label={t("product.form.tax.title")}>
                {productDetails?.haveTax ? `${productDetails?.tax}%` : t("product.form.tax.without")}
              </Descriptions.Item>
              <Descriptions.Item label={t("createDate")}>
                {toJalali({ date: productDetails?.createdOn, toFormat: dateFormats.ui.jalaliDate }).string}
              </Descriptions.Item>
              <Descriptions.Item label={t("quantity.title")}>
                {productDetails?.unlimited
                  ? t("quantity.unlimited")
                  : `${String(productDetails?.quantity || 0)} ${t("number")}`}
              </Descriptions.Item>
            </Descriptions>
          )}
        </Col>
        <Col xs={24}>
          {loadingLink ? (
            <Skeleton active paragraph />
          ) : (
            <Descriptions className="tw:w-full" column={1} bordered title={t("product.detail.paySpecification")}>
              <Descriptions.Item label={t("product.detail.perma.paidBuyersCount")}>
                {linkDetails?.paidBuyersCount
                  ? String(linkDetails.paidBuyersCount)
                  : t("product.detail.perma.noPayment")}
              </Descriptions.Item>
              <Descriptions.Item label={t("product.detail.perma.payCount")}>
                {linkDetails?.buyerCounts ? String(linkDetails.buyerCounts) : t("product.detail.perma.noPayment")}
              </Descriptions.Item>
              <Descriptions.Item label={t("product.detail.perma.payTotal")}>
                {linkDetails?.buyerSum ? String(linkDetails.buyerSum) : t("product.detail.perma.noPayment")}
              </Descriptions.Item>
              <Descriptions.Item label={t("product.form.perma.redirectPage")}>
                {linkDetails?.redirectPage ? linkDetails.redirectPage : t("undefined")}
              </Descriptions.Item>
              <Descriptions.Item label={t("product.detail.perma.name")}>
                {t(LinkQuestionOptions[linkDetails?.nameOption || 0])}
              </Descriptions.Item>
              <Descriptions.Item label={t("product.detail.perma.email")}>
                {t(LinkQuestionOptions[linkDetails?.emailOption || 0])}
              </Descriptions.Item>
              <Descriptions.Item label={t("product.detail.perma.phone")}>
                {t(LinkQuestionOptions[linkDetails?.phoneOption || 0])}
              </Descriptions.Item>
              <Descriptions.Item label={t("product.detail.perma.address")}>
                {t(LinkQuestionOptions[linkDetails?.getAddress ? 1 : 0])}
              </Descriptions.Item>
              <Descriptions.Item label={t("product.detail.perma.customQuestion")}>
                {t(LinkQuestionOptions[linkDetails?.customDesOption || 0])}
              </Descriptions.Item>
              <Descriptions.Item label={t("product.detail.perma.customQuestionText")}>
                {linkDetails?.customDescriptionText}
              </Descriptions.Item>
            </Descriptions>
          )}
        </Col>
      </Row>
      {contextHolder}
    </Drawer>
  );
};

export default ProductDetail;
